import React, { useState, useRef, useEffect, useMemo } from 'react';
import JoditEditor from 'jodit-react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from '../CSS/PagesSettings.module.css';
import { usePageContent } from '../../../../hooks/usePagesSettings';

const PagesSettings = () => {
  const [activeTab, setActiveTab] = useState('privacy');
  const [localContent, setLocalContent] = useState('');
  const editor = useRef(null);

  const {
    pageContent: privacyContent,
    isLoading: privacyLoading,
    updateContent: updatePrivacy,
    isUpdating: isUpdatingPrivacy
  } = usePageContent('privacy');

  const {
    pageContent: termsContent,
    isLoading: termsLoading,
    updateContent: updateTerms,
    isUpdating: isUpdatingTerms
  } = usePageContent('terms');

  const {
    pageContent: refundContent,
    isLoading: refundLoading,
    updateContent: updateRefund,
    isUpdating: isUpdatingRefund
  } = usePageContent('refund');

  useEffect(() => {
    let currentContent = '';
    if (activeTab === 'privacy') {
      currentContent = privacyContent?.content;
    } else if (activeTab === 'terms') {
      currentContent = termsContent?.content;
    } else if (activeTab === 'refund') {
      currentContent = refundContent?.content;
    }
    
    setLocalContent(currentContent || '');
  }, [activeTab, privacyContent, termsContent, refundContent]);

    const config = useMemo(() => ({
      theme: 'dark',
      height: 400,
      enableDragAndDropFileToEditor: true,
      showXPathInStatusbar: false,
      buttons: [
        'source', '|',
        'bold', 'italic', 'underline', 'strikethrough', '|',
        'font', 'fontsize', 'brush', 'paragraph', '|',
        'ul', 'ol', '|',
        'table', 'link', '|',
        'left', 'center', 'right', 'justify', '|',
        'undo', 'redo', '|',
        'hr', 'eraser', 'fullsize'
      ],
      uploader: {
        insertImageAsBase64URI: true
      },
      colors: {
        background: '#222222',
        text: '#ffffff',
        defaultTextColor: '#ffffff',
        defaultBackgroundColor: '#222222'
      },
      style: {
        background: '#222222',
        color: '#ffffff'
      },
      statusbar: false,
      readonly: false,
      toolbarSticky: true,
      showPlaceholder: false,
      saveIndicator: false,
      maxHeight: 800,
      minHeight: 400,
      useSearch: false,
      spellcheck: false,
      editorCssClass: 'dark-theme',
    }), []);

  const handleTabChange = (tab) => {
    if (tab !== activeTab) {
      setActiveTab(tab);
    }
  };

  const handleSave = async () => {
    try {
      if (activeTab === 'privacy') {
        await updatePrivacy(localContent);
      } else if (activeTab === 'terms') {
        await updateTerms(localContent);
      } else if (activeTab === 'refund') {
        await updateRefund(localContent);
      }
    } catch (error) {
      console.error('Failed to save content:', error);
    }
  };

  const handleCancel = () => {
    let currentContent = '';
    if (activeTab === 'privacy') {
      currentContent = privacyContent?.content;
    } else if (activeTab === 'terms') {
      currentContent = termsContent?.content;
    } else if (activeTab === 'refund') {
      currentContent = refundContent?.content;
    }
    
    setLocalContent(currentContent || '');
  };

  const isLoading = activeTab === 'privacy' ? privacyLoading : 
                   activeTab === 'terms' ? termsLoading : refundLoading;
  const isUpdating = activeTab === 'privacy' ? isUpdatingPrivacy : 
                    activeTab === 'terms' ? isUpdatingTerms : isUpdatingRefund;

  return (
    <div className={styles.pagesContainer}>
      <ToastContainer />
      
      <h1 className={styles.header}>Page Settings</h1>
      
      <div className={styles.tabContainer}>
        <button
          className={`${styles.tabButton} ${activeTab === 'privacy' ? styles.activeTab : ''}`}
          onClick={() => handleTabChange('privacy')}
          disabled={isUpdating}
        >
          Privacy Policy
        </button>
        <button
          className={`${styles.tabButton} ${activeTab === 'terms' ? styles.activeTab : ''}`}
          onClick={() => handleTabChange('terms')}
          disabled={isUpdating}
        >
          Terms & Conditions
        </button>
        <button
          className={`${styles.tabButton} ${activeTab === 'refund' ? styles.activeTab : ''}`}
          onClick={() => handleTabChange('refund')}
          disabled={isUpdating}
        >
          Refund Policy
        </button>
      </div>

      <div className={styles.editorContainer}>
        <label className={styles.editorLabel}>
          {activeTab === 'privacy' ? 'Privacy Policy Content' : 
           activeTab === 'terms' ? 'Terms & Conditions Content' : 
           'Refund Policy Content'}
        </label>
        
        <div className={styles.editorWrapper}>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <span>Loading content...</span>
            </div>
          ) : (
            <JoditEditor
              ref={editor}
              value={localContent}
              config={config}
              onBlur={(newContent) => setLocalContent(newContent)}
              onChange={(newContent) => setLocalContent(newContent)}
            />
          )}
        </div>
      </div>

      <div className={styles.actionButtons}>
        <button 
          className={styles.cancelButton} 
          onClick={handleCancel}
          disabled={isLoading || isUpdating}
        >
          Cancel
        </button>
        <button 
          className={styles.saveButton} 
          onClick={handleSave}
          disabled={isLoading || isUpdating}
        >
          {isUpdating ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {isUpdating && (
        <div className={styles.savingOverlay}>
          <div className={styles.savingSpinner} />
          <span>Saving changes...</span>
        </div>
      )}
    </div>
  );
};

export default PagesSettings;